/**
 * Parâmetros da folha (camada 1 — dados versionados com vigência), padrão
 * Mantine (U5): vigências por chave exibidas de forma legível (nunca JSON), nova
 * vigência com formulário tipado + editor de faixas, e alerta de buraco de
 * cobertura via diagnóstico do gate.
 */
import { useEffect, useState } from "react";
import { Alert, Button, Card, Table, Text, Title } from "@mantine/core";
import { api } from "../api";
import { fmtCompetencia } from "../format";
import { CompetenciaField } from "../componentes/entradas-mantine";
import { NovaVigenciaParametro, resumoFaixas } from "../componentes/ParametroForm";
import { notificarOk, AvisosAlert } from "../componentes/notificacoes";
import { Carregando } from "../componentes/Estados";
import { TabelaDados, Th } from "../componentes/listas";
import { formatarPayload, shapeDaChave } from "../parametros-shapes";

interface Faixa {
  ordem: number;
  limiteInferiorCentavos: number;
  limiteSuperiorCentavos: number | null;
  aliquotaPpm: number;
  parcelaDeduzirCentavos: number | null;
}
interface Parametro {
  id: string;
  chave: string;
  vigenciaInicio: number;
  vigenciaFim: number | null;
  payload: Record<string, unknown>;
  faixas: Faixa[];
}
interface Gate {
  elegiveis: string[];
  sugerido: string | null;
  problemasGerais: string[];
  diagnosticos: { motor: string; compativel: boolean; problemas: string[] }[];
}

function competenciaAtual(): number {
  const hoje = new Date();
  return hoje.getUTCFullYear() * 100 + (hoje.getUTCMonth() + 1);
}

export function ParametrosPage() {
  const [lista, setLista] = useState<Parametro[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [compRef, setCompRef] = useState<number | null>(competenciaAtual());
  const [gate, setGate] = useState<Gate | null>(null);
  const [baseEdicao, setBaseEdicao] = useState<{ chave: string; payload: Record<string, unknown>; faixas: Faixa[] } | null>(null);
  const [formKey, setFormKey] = useState(0);
  // P2 — avisos de carga histórica da última vigência criada (meses fechados cobertos)
  const [avisosVigencia, setAvisosVigencia] = useState<string[]>([]);

  async function carregar() {
    try {
      setErro(null);
      const r = await api.get<{ parametros: Parametro[] }>("/parametros");
      setLista(r.parametros);
    } catch (e) {
      setErro(String(e));
    }
  }

  async function verificarCobertura(comp: number | null) {
    setCompRef(comp);
    if (!comp) {
      setGate(null);
      return;
    }
    try {
      setGate(await api.get<Gate>(`/competencias/motores-elegiveis?competencia=${comp}`));
    } catch (e) {
      setErro(String(e));
    }
  }

  useEffect(() => {
    void carregar();
    void verificarCobertura(competenciaAtual());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const porChave = new Map<string, Parametro[]>();
  for (const p of lista ?? []) {
    const g = porChave.get(p.chave) ?? [];
    g.push(p);
    porChave.set(p.chave, g);
  }
  const chaves = [...porChave.keys()].sort((a, b) => a.localeCompare(b, "pt-BR"));

  return (
    <>
      <Title order={2} mb={4}>Parâmetros da folha</Title>
      <Text size="sm" c="dimmed" mb="md">
        Tabelas e valores com vigência por competência (camada 1 da arquitetura). Mudou uma tabela ou um valor? Crie uma
        nova vigência a partir do mês em que ela passa a valer — o histórico fica intacto.
      </Text>
      {erro && (
        <Alert color="red" title="Erro" mb="sm" role="alert">
          {erro}
        </Alert>
      )}
      <AvisosAlert avisos={avisosVigencia} />

      <Card withBorder shadow="xs" mb="md">
        <Title order={4} mb={2}>Cobertura de uma competência</Title>
        <Text size="sm" c="dimmed" mb="sm">
          Verifica se todos os parâmetros têm vigência cobrindo o mês escolhido (o gate de abertura exige isso).
        </Text>
        <CompetenciaField valor={compRef} onChange={(c) => void verificarCobertura(c)} />
        {gate && (
          <>
            {gate.problemasGerais.length > 0 && (
              <Alert color="red" title="Bloqueios gerais" mt="sm">
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {gate.problemasGerais.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </Alert>
            )}
            {gate.diagnosticos.map((d) =>
              d.compativel ? (
                <Alert color="sillis" key={d.motor} mt="sm">
                  {d.motor}: tudo coberto{d.motor === gate.sugerido ? " (motor sugerido)" : ""}
                </Alert>
              ) : (
                <Alert color="red" title={`${d.motor}: faltam vigências`} key={d.motor} mt="sm">
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {d.problemas.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                </Alert>
              ),
            )}
          </>
        )}
      </Card>

      <Card withBorder shadow="xs" mb="md">
        <Title order={4} mb="sm">Nova vigência</Title>
        <NovaVigenciaParametro
          key={`${formKey}-${baseEdicao?.chave ?? "novo"}`}
          chaveInicial={baseEdicao?.chave}
          payloadExistente={baseEdicao?.payload ?? null}
          faixasExistentes={
            baseEdicao
              ? baseEdicao.faixas.map((f) => ({
                  deCentavos: f.limiteInferiorCentavos,
                  ateCentavos: f.limiteSuperiorCentavos,
                  aliquotaPpm: f.aliquotaPpm,
                  parcelaCentavos: f.parcelaDeduzirCentavos,
                }))
              : null
          }
          onCriado={async (mensagem, avisos) => {
            notificarOk(mensagem);
            setAvisosVigencia(avisos);
            setBaseEdicao(null);
            setFormKey((k) => k + 1);
            await carregar();
            await verificarCobertura(compRef);
          }}
          onErro={(e) => {
            setErro(String((e as { corpo?: { erro?: string } })?.corpo?.erro ?? e));
          }}
        />
      </Card>

      <Title order={3} mb="sm">Vigências cadastradas</Title>
      {!lista ? (
        <Carregando linhas={5} />
      ) : (
        chaves.map((c) => {
          const shape = shapeDaChave(c);
          return (
            <Card key={c} withBorder shadow="xs" mb="sm">
              <Title order={4} mb="xs">{shape?.rotulo ?? c}</Title>
              <TabelaDados>
                <Table.Thead>
                  <Table.Tr>
                    <Th>Vigência</Th>
                    <Th>Valores</Th>
                    <Th></Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(porChave.get(c) ?? []).map((p) => (
                    <Table.Tr key={p.id}>
                      <Table.Td style={{ whiteSpace: "nowrap" }}>
                        {fmtCompetencia(p.vigenciaInicio)} → {p.vigenciaFim ? fmtCompetencia(p.vigenciaFim) : "em vigor"}
                      </Table.Td>
                      <Table.Td>
                        {shape ? (
                          <>
                            {formatarPayload(shape, p.payload).map((l) => (
                              <Text key={l.rotulo} size="sm">
                                {l.rotulo}: <strong>{l.valor}</strong>
                              </Text>
                            ))}
                            {p.faixas.length > 0 && (
                              <Text size="xs" c="dimmed" mt={4}>
                                Faixas: {resumoFaixas(shape, p.faixas)}
                              </Text>
                            )}
                          </>
                        ) : (
                          <Text size="xs" c="dimmed">(chave sem formulário cadastrado)</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {shape && (
                          <Button
                            size="xs"
                            variant="light"
                            onClick={() => {
                              setBaseEdicao({ chave: c, payload: p.payload, faixas: p.faixas });
                              setFormKey((k) => k + 1);
                              window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                            title="Pré-preenche o formulário com estes valores para criar a próxima vigência"
                          >
                            usar como base
                          </Button>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </TabelaDados>
            </Card>
          );
        })
      )}
    </>
  );
}
